import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import {
  Zap,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Menu,
  X,
  ChevronDown,
  Shield,
  Award,
  Clock,
  Users,
  Building2,
  Cable,
  Lightbulb,
  Wrench,
  CheckCircle2,
  Send,
  ExternalLink,
  Star,
  Briefcase,
  GraduationCap,
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_fcbe1d8f-13f1-4055-b649-6af60e22c882/artifacts/5ohazwmk_Shrishaila%20Logo.png";

const HERO_BG =
  "https://images.pexels.com/photos/36137497/pexels-photo-36137497.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1595831708961-1b13c0dd2422?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxlbGVjdHJpY2lhbiUyMGVuZ2luZWVyJTIwY29uc3RydWN0aW9uJTIwd29ya2luZ3xlbnwwfHx8fDE3NzYxNjE1NTd8MA&ixlib=rb-4.1.0&q=85";

const SERVICE_IMG1 =
  "https://images.unsplash.com/photo-1630609531004-61ff953bae2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxlbGVjdHJpY2FsJTIwdG93ZXIlMjBwb3dlciUyMGxpbmVzfGVufDB8fHx8MTc3NjE2MTU0MXww&ixlib=rb-4.1.0&q=85";

const SERVICE_IMG2 =
  "https://images.pexels.com/photos/25537595/pexels-photo-25537595.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

/* ─── useInView hook ─── */
function useInView(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, isVisible];
}

/* ─── HEADER ─── */
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Clientele", href: "#clientele" },
    { label: "Promoters", href: "#promoters" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl shadow-sm border-b border-[#E2E8F0]/50"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <a
            href="#hero"
            data-testid="header-logo"
            className="flex items-center gap-3"
          >
            <img src={LOGO_URL} alt="Shrishaila Electricals" className="h-16 w-auto" />
            <div className="hidden sm:block">
              <div
                className={`font-black text-lg tracking-tight leading-tight ${
                  scrolled ? "text-[#0F172A]" : "text-white"
                }`}
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                SHRISHAILA
              </div>
              <div
                className={`text-[13px] font-bold tracking-[0.15em] uppercase ${
                  scrolled ? "text-[#D32F2F]" : "text-[#D32F2F]"
                }`}
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                ELECTRICALS
              </div>
              <div
                className={`text-[11px] tracking-wide leading-tight mt-0.5 ${
                  scrolled ? "text-[#64748B]" : "text-white/60"
                }`}
              >
                ಶ್ರೀಶೈಲ ಎಲೆಕ್ಟ್ರಿಕಲ್ಸ್
              </div>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="nav-link"
                style={{
                  color: scrolled ? "#0F172A" : "rgba(255,255,255,0.9)",
                }}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-cta-button"
              className="cta-btn text-xs py-3 px-6"
            >
              Get a Quote <ArrowRight size={14} />
            </a>
          </nav>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? (
              <X size={28} className={scrolled ? "text-[#0F172A]" : "text-white"} />
            ) : (
              <Menu size={28} className={scrolled ? "text-[#0F172A]" : "text-white"} />
            )}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button
            data-testid="mobile-menu-close"
            className="absolute top-6 right-6"
            onClick={() => setMenuOpen(false)}
          >
            <X size={32} className="text-[#0F172A]" />
          </button>
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setMenuOpen(false)}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            className="cta-btn mt-4"
            onClick={() => setMenuOpen(false)}
            data-testid="mobile-nav-cta"
          >
            Get a Quote <ArrowRight size={16} />
          </a>
        </div>
      )}
    </>
  );
};

/* ─── HERO ─── */
const Hero = () => {
  const [ref, visible] = useInView();
  return (
    <section id="hero" className="hero-section" data-testid="hero-section" ref={ref}>
      <div className="hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }} />
      <div className="hero-overlay" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-0">
        <div className="max-w-3xl">
          <div
            className={`opacity-0 ${visible ? "animate-fade-in-up" : ""}`}
            style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}
          >
            <span
              className="inline-block text-[#D32F2F] text-xs font-bold uppercase tracking-[0.25em] mb-6"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Hallmark of Quality &mdash; Class I Government Contractor &mdash; Est. 2001
            </span>
          </div>

          <h1
            className={`text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05] mb-6 opacity-0 ${
              visible ? "animate-fade-in-up" : ""
            }`}
            style={{
              fontFamily: "Chivo, sans-serif",
              animationDelay: "0.25s",
              animationFillMode: "forwards",
            }}
          >
            From Concept
            <br />
            to{" "}
            <span className="text-[#D32F2F]">Commissioning</span>
          </h1>

          <p
            className={`text-base sm:text-lg text-white/70 max-w-xl mb-10 leading-relaxed opacity-0 ${
              visible ? "animate-fade-in-up" : ""
            }`}
            style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}
          >
            Powering Karnataka's electrical infrastructure for over 25 years.
            BESCOM-approved Class I contractor delivering turnkey HT/LT solutions
            with precision, reliability, and our hallmark of quality.
          </p>

          <div
            className={`flex flex-wrap gap-4 opacity-0 ${
              visible ? "animate-fade-in-up" : ""
            }`}
            style={{ animationDelay: "0.55s", animationFillMode: "forwards" }}
          >
            <a href="#contact" className="cta-btn" data-testid="hero-cta-button">
              Request a Quote <ArrowRight size={16} />
            </a>
            <a href="#services" className="cta-btn-outline" data-testid="hero-services-button">
              Our Services <ChevronDown size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <a
          href="#about"
          data-testid="scroll-indicator"
          className="flex flex-col items-center gap-2 text-white/40 hover:text-white/70 transition-colors"
        >
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ fontFamily: "Chivo" }}>
            Scroll
          </span>
          <ChevronDown size={20} className="animate-bounce" />
        </a>
      </div>
    </section>
  );
};

/* ─── ABOUT ─── */
const About = () => {
  const [ref, visible] = useInView();
  return (
    <section
      id="about"
      className="py-20 md:py-32 bg-white"
      data-testid="about-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div
            className={`relative opacity-0 ${visible ? "animate-slide-left" : ""}`}
            style={{ animationFillMode: "forwards" }}
          >
            <div className="relative">
              <img
                src={ABOUT_IMG}
                alt="Shrishaila Electricals team at work"
                className="w-full h-[420px] object-cover"
                data-testid="about-image"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#D32F2F] text-white p-6 hidden md:block">
                <div
                  className="text-4xl font-black"
                  style={{ fontFamily: "Chivo" }}
                >
                  25+
                </div>
                <div className="text-xs uppercase tracking-[0.15em] font-bold mt-1">
                  Years of Excellence
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div
            className={`opacity-0 ${visible ? "animate-slide-right" : ""}`}
            style={{ animationFillMode: "forwards", animationDelay: "0.2s" }}
          >
            <span className="section-label">About Us</span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-6"
              style={{ fontFamily: "Chivo" }}
            >
              Trusted Electrical Engineering
              <br />
              and Contract Company Since 2001
            </h2>
            <div className="inline-flex items-center gap-2 bg-[#FEF2F2] border border-[#D32F2F]/20 px-4 py-2 mb-6">
              <Star size={14} className="text-[#D32F2F]" />
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#D32F2F]" style={{ fontFamily: "Chivo" }}>
                Hallmark of Quality
              </span>
            </div>
            <p className="text-base leading-relaxed text-[#64748B] mb-6">
              <strong className="text-[#0F172A]">Shrishaila Electricals (India) Private Limited</strong> is
              a registered Class I contractor with the Government of Karnataka and Bangalore Electricity
              Supply Company Limited (BESCOM). Headquartered in Bangalore, we specialize in the complete
              spectrum of electrical infrastructure projects.
            </p>
            <p className="text-base leading-relaxed text-[#64748B] mb-8">
              With a current unexecuted order book of{" "}
              <strong className="text-[#D32F2F]">Rs. 130 Crore</strong>, we deliver turnkey solutions
              encompassing supply, installation, testing, and commissioning of electrical works. Our
              enduring association with BESCOM stands as a testament to our reliability and quality of execution.
            </p>

            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: <Award size={20} />, label: "CARE B+ Rated" },
                { icon: <Shield size={20} />, label: "Class I Contractor" },
                { icon: <Building2 size={20} />, label: "BESCOM Approved" },
                { icon: <Users size={20} />, label: "Experienced Team" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FEF2F2] text-[#D32F2F]">
                    {item.icon}
                  </div>
                  <span className="text-sm font-semibold text-[#0F172A]">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Leadership Team */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <span className="section-label">Leadership Team</span>
            <h3
              className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0F172A]"
              style={{ fontFamily: "Chivo" }}
            >
              Driving Excellence at Every Level
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0">
            {[
              {
                name: "R. Jagadeesh",
                role: "Managing Director",
                desc: "Provides strategic vision and overall leadership, steering the company's growth trajectory and fostering key relationships with government bodies and DISCOMs across Karnataka.",
              },
              {
                name: "Sandesh",
                role: "Finance & Business Development",
                desc: "Oversees financial planning, fund management, and drives new business opportunities through tender identification, client engagement, and strategic partnerships.",
              },
              {
                name: "Satish",
                role: "Project Management & Execution",
                desc: "Leads end-to-end project planning, resource allocation, and on-ground execution ensuring timely delivery, cost control, and adherence to BESCOM quality standards.",
              },
              {
                name: "Basavaraj",
                role: "Project Execution",
                desc: "Manages field-level operations, supervises construction teams, and ensures all HT/LT line works and substation installations meet safety and technical specifications.",
              },
              {
                name: "Pavithra",
                role: "Procurement & Inventory",
                desc: "Handles sourcing and procurement of electrical materials, equipment, and RMUs, managing inventory levels, ensuring quality compliance, vendor management, and cost-effective supply chain operations.",
              },
            ].map((person, i) => (
              <div
                key={i}
                className="border border-[#E2E8F0] p-6 bg-white hover:bg-[#0A0A0A] hover:text-white group transition-all duration-300"
                data-testid={`leader-card-${i}`}
              >
                <div className="w-11 h-11 flex items-center justify-center bg-[#D32F2F] text-white font-black text-sm mb-4" style={{ fontFamily: "Chivo" }}>
                  {person.name.charAt(0)}
                </div>
                <h4
                  className="text-base font-bold text-[#0F172A] group-hover:text-white tracking-tight mb-1"
                  style={{ fontFamily: "Chivo" }}
                >
                  {person.name}
                </h4>
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#D32F2F] mb-3"
                  style={{ fontFamily: "Chivo" }}
                >
                  {person.role}
                </div>
                <p className="text-xs leading-relaxed text-[#64748B] group-hover:text-white/60">
                  {person.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── SERVICES ─── */
const services = [
  {
    icon: <Cable size={28} />,
    title: "HT/LT Line Construction",
    desc: "Design, supply, and erection of High Tension and Low Tension overhead and underground electrical lines for BESCOM distribution networks.",
  },
  {
    icon: <Zap size={28} />,
    title: "Substation Installation",
    desc: "Complete turnkey construction of electrical substations including transformers, switchgear, protection systems, and control panels.",
  },
  {
    icon: <Lightbulb size={28} />,
    title: "Rural Electrification",
    desc: "Extending electrical infrastructure to rural areas under government schemes, enabling reliable power access across Karnataka.",
  },
  {
    icon: <Building2 size={28} />,
    title: "Trading in RMU",
    desc: "Trading in Ring Main Units (RMU) of Schneider Electric brand, specifically catering to tendered business requirements for reliable power distribution networks.",
  },
  {
    icon: <Wrench size={28} />,
    title: "Testing & Commissioning",
    desc: "Rigorous testing, calibration, and commissioning of newly installed electrical systems to ensure safety and performance standards.",
  },
  {
    icon: <Clock size={28} />,
    title: "Maintenance & AMC",
    desc: "Annual maintenance contracts and periodical preventive and breakdown maintenances of RMU's & HT/LT infrastructure for uninterrupted power supply.",
  },
];

const Services = () => {
  const [ref, visible] = useInView();
  return (
    <section
      id="services"
      className="py-20 md:py-32 bg-[#F4F4F5]"
      data-testid="services-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className={`max-w-2xl mb-16 opacity-0 ${visible ? "animate-fade-in-up" : ""}`}
          style={{ animationFillMode: "forwards" }}
        >
          <span className="section-label">Our Services</span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-4"
            style={{ fontFamily: "Chivo" }}
          >
            End-to-End Electrical
            <br />
            Infrastructure Solutions
          </h2>
          <p className="text-base leading-relaxed text-[#64748B]">
            From concept to commissioning, we cover every phase of electrical project execution
            with precision engineering and BESCOM-compliant standards.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
          {services.map((s, i) => (
            <div
              key={i}
              className={`grid-border-card bg-white p-8 opacity-0 ${
                visible ? "animate-fade-in-up" : ""
              }`}
              style={{
                animationDelay: `${0.1 + i * 0.1}s`,
                animationFillMode: "forwards",
              }}
              data-testid={`service-card-${i}`}
            >
              <div className="text-[#D32F2F] mb-5">{s.icon}</div>
              <h3
                className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight"
                style={{ fontFamily: "Chivo" }}
              >
                {s.title}
              </h3>
              <p className="text-sm leading-relaxed text-[#64748B]">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Service images row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
          <div className="relative overflow-hidden group" data-testid="service-image-1">
            <img
              src={SERVICE_IMG1}
              alt="Power transmission lines"
              className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <div
                className="text-lg font-bold"
                style={{ fontFamily: "Chivo" }}
              >
                HT/LT Line Projects
              </div>
              <div className="text-xs text-white/70 uppercase tracking-wider">
                Across Karnataka
              </div>
            </div>
          </div>
          <div className="relative overflow-hidden group" data-testid="service-image-2">
            <img
              src={SERVICE_IMG2}
              alt="Electrical substation"
              className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <div
                className="text-lg font-bold"
                style={{ fontFamily: "Chivo" }}
              >
                Substation Works
              </div>
              <div className="text-xs text-white/70 uppercase tracking-wider">
                Turnkey Execution
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── STATS ─── */
const stats = [
  { value: "130", label: "Order Book Value", suffix: "Cr+", isCurrency: true },
  { value: "15", label: "Years of Operations", suffix: "+" },
  { value: "100", label: "Projects Delivered", suffix: "+" },
  { value: "1", label: "BESCOM Class", suffix: "" },
];

const Stats = () => {
  const [ref, visible] = useInView();
  return (
    <section
      id="stats"
      className="bg-[#0A0A0A]"
      data-testid="stats-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4">
          {stats.map((s, i) => (
            <div
              key={i}
              className={`stat-item text-center opacity-0 ${
                visible ? "animate-fade-in-up" : ""
              }`}
              style={{
                animationDelay: `${0.1 + i * 0.15}s`,
                animationFillMode: "forwards",
              }}
              data-testid={`stat-item-${i}`}
            >
              <div
                className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "Chivo" }}
              >
                {s.isCurrency && <span className="text-[#D32F2F]">{"\u20B9"}</span>}
                {s.value}
                {s.suffix && <span className="text-[#D32F2F]">{s.suffix}</span>}
              </div>
              <div
                className="text-[10px] sm:text-xs text-white/50 uppercase tracking-[0.2em] font-bold"
                style={{ fontFamily: "Chivo" }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── WHY CHOOSE US ─── */
const whyItems = [
  {
    icon: <Shield size={32} />,
    title: "BESCOM Approved",
    desc: "Registered Class I contractor with an unbroken track record of compliance and quality with Bangalore Electricity Supply Company.",
  },
  {
    icon: <Award size={32} />,
    title: "CARE Rated",
    desc: "CARE B+; Stable rating reaffirmed for long-term bank facilities, reflecting our financial discipline and operational stability.",
  },
  {
    icon: <Clock size={32} />,
    title: "Timely Delivery",
    desc: "Proven project management capabilities ensuring on-time completion of electrical infrastructure projects across Karnataka.",
  },
  {
    icon: <CheckCircle2 size={32} />,
    title: "Safety First",
    desc: "Rigorous safety protocols and compliance with KERC standards at every stage of project execution and commissioning.",
  },
];

const WhyChooseUs = () => {
  const [ref, visible] = useInView();
  return (
    <section
      id="why-us"
      className="py-20 md:py-32 bg-white"
      data-testid="why-us-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className={`text-center max-w-2xl mx-auto mb-16 opacity-0 ${
            visible ? "animate-fade-in-up" : ""
          }`}
          style={{ animationFillMode: "forwards" }}
        >
          <span className="section-label">Why Choose Us</span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0F172A]"
            style={{ fontFamily: "Chivo" }}
          >
            Built on Trust,
            <br />
            Powered by Expertise
          </h2>
          <p className="text-base text-[#64748B] mt-4 leading-relaxed">
            Our <span className="font-bold text-[#D32F2F]">Hallmark of Quality</span> drives every project we undertake.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {whyItems.map((item, i) => (
            <div
              key={i}
              className={`why-card opacity-0 ${visible ? "animate-fade-in-up" : ""}`}
              style={{
                animationDelay: `${0.15 + i * 0.1}s`,
                animationFillMode: "forwards",
              }}
              data-testid={`why-card-${i}`}
            >
              <div className="why-icon text-[#D32F2F] mb-5">{item.icon}</div>
              <h3
                className="why-title text-lg font-bold text-[#0F172A] mb-3 tracking-tight"
                style={{ fontFamily: "Chivo" }}
              >
                {item.title}
              </h3>
              <p className="why-desc text-sm leading-relaxed text-[#64748B]">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── CLIENTELE ─── */
const clients = [
  {
    name: "BESCOM",
    full: "Bangalore Electricity Supply Company Limited",
    region: "Bangalore Region",
    logo: "/logos/bescom.png",
    color: "#1565C0",
  },
  {
    name: "MESCOM",
    full: "Mangalore Electricity Supply Company Limited",
    region: "Mangalore Region",
    logo: "/logos/mescom.png",
    color: "#2E7D32",
  },
  {
    name: "HESCOM",
    full: "Hubli Electricity Supply Company Limited",
    region: "Hubli-Dharwad Region",
    logo: "https://customer-assets.emergentagent.com/job_shrishaila-solutions/artifacts/9i1wxzqj_HESCOM%20LOGO.jpg",
    color: "#E65100",
  },
  {
    name: "CHESCOM",
    full: "Chamundeshwari Electricity Supply Corporation",
    region: "Mysuru Region",
    logo: "/logos/chescom.png",
    color: "#6A1B9A",
  },
  {
    name: "BMRCL",
    full: "Bangalore Metro Rail Corporation Limited",
    region: "Bangalore Metro",
    logo: "https://customer-assets.emergentagent.com/job_shrishaila-solutions/artifacts/cfyms387_bmrcl%20logo.png",
    color: "#7B1FA2",
  },
  {
    name: "BDA / GBA",
    full: "Bangalore Development Authority (now Greater Bangalore Authority)",
    region: "Bangalore",
    logo: "https://customer-assets.emergentagent.com/job_shrishaila-solutions/artifacts/pqatuu4v_bda%20Logo.png",
    color: "#0D47A1",
  },
  {
    name: "KIADB",
    full: "Karnataka Industrial Area Development Board",
    region: "Statewide",
    logo: "https://customer-assets.emergentagent.com/job_shrishaila-solutions/artifacts/tzhku27e_kiadb_logo.png",
    color: "#1B5E20",
  },
  {
    name: "KPTCL",
    full: "Karnataka Power Transmission Corporation Limited",
    region: "Statewide",
    logo: "https://customer-assets.emergentagent.com/job_shrishaila-solutions/artifacts/ti1wbal0_KPTCL%20logo.png",
    color: "#BF360C",
  },
];

const ClientLogo = ({ client, index, visible }) => {
  const [imgError, setImgError] = useState(false);
  return (
    <div
      className={`flex flex-col items-center p-6 md:p-8 border border-[#E2E8F0] bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 opacity-0 ${
        visible ? "animate-fade-in-up" : ""
      }`}
      style={{
        animationDelay: `${0.1 + index * 0.1}s`,
        animationFillMode: "forwards",
      }}
      data-testid={`client-card-${client.name.toLowerCase()}`}
    >
      {client.logo && !imgError ? (
        <img
          src={client.logo}
          alt={`${client.name} logo`}
          className="h-16 w-auto object-contain mb-4"
          onError={() => setImgError(true)}
        />
      ) : (
        <div
          className="w-16 h-16 flex items-center justify-center mb-4 text-white font-black text-lg"
          style={{ background: client.color, fontFamily: "Chivo" }}
        >
          {client.name.charAt(0)}
        </div>
      )}
      <h3
        className="text-base font-bold text-[#0F172A] tracking-tight"
        style={{ fontFamily: "Chivo" }}
      >
        {client.name}
      </h3>
      <p className="text-xs text-[#64748B] text-center mt-1 leading-relaxed">
        {client.full}
      </p>
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#D32F2F] font-bold mt-2" style={{ fontFamily: "Chivo" }}>
        {client.region}
      </span>
    </div>
  );
};

const Clientele = () => {
  const [ref, visible] = useInView();
  return (
    <section
      id="clientele"
      className="py-20 md:py-32 bg-[#F4F4F5]"
      data-testid="clientele-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className={`text-center max-w-2xl mx-auto mb-16 opacity-0 ${
            visible ? "animate-fade-in-up" : ""
          }`}
          style={{ animationFillMode: "forwards" }}
        >
          <span className="section-label">Our Clientele</span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-4"
            style={{ fontFamily: "Chivo" }}
          >
            Trusted by Karnataka's
            <br />
            Leading Institutions
          </h2>
          <p className="text-base leading-relaxed text-[#64748B]">
            Delivering our <span className="font-bold text-[#D32F2F]">Hallmark of Quality</span> to
            the state's premier electricity distribution and infrastructure organizations.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-0">
          {clients.map((client, i) => (
            <ClientLogo key={client.name} client={client} index={i} visible={visible} />
          ))}
        </div>

        <div
          className={`mt-12 text-center opacity-0 ${visible ? "animate-fade-in-up" : ""}`}
          style={{ animationDelay: "0.7s", animationFillMode: "forwards" }}
        >
          <div className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-8 py-4">
            <Star size={18} className="text-[#D32F2F]" />
            <span className="text-sm font-bold uppercase tracking-[0.1em]" style={{ fontFamily: "Chivo" }}>
              100% Government &amp; DISCOM Clientele &mdash; Hallmark of Quality
            </span>
            <Star size={18} className="text-[#D32F2F]" />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── PROMOTERS ─── */
const promoters = [
  {
    name: "Mr. R. Jagadish",
    role: "Co-Founder & Managing Director",
    desc: "Qualified electrical engineering graduate with over two decades of hands-on experience in the electrical contracting industry. Founded Shrishaila Electricals as a proprietorship in 2001 and led its transformation into a Private Limited company in 2009.",
    tenure: "20+ Years",
    icon: <Briefcase size={24} />,
  },
  {
    name: "Mrs. R. Shylaja Jagadish",
    role: "Co-Founder & Director",
    desc: "Qualified electrical engineering graduate who co-founded the company alongside Mr. Jagadish. Instrumental in establishing the company's reputation for quality and compliance with BESCOM standards.",
    tenure: "20+ Years",
    icon: <GraduationCap size={24} />,
  },
  {
    name: "Mr. Shashikumar Puttappa Mugur",
    role: "Director",
    desc: "Brings 16 years of industry expertise to the board, overseeing project execution and ensuring adherence to the company's hallmark of quality across all operations.",
    tenure: "16 Years",
    icon: <Briefcase size={24} />,
  },
];

const Promoters = () => {
  const [ref, visible] = useInView();
  return (
    <section
      id="promoters"
      className="py-20 md:py-32 bg-white"
      data-testid="promoters-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className={`text-center max-w-2xl mx-auto mb-16 opacity-0 ${
            visible ? "animate-fade-in-up" : ""
          }`}
          style={{ animationFillMode: "forwards" }}
        >
          <span className="section-label">Leadership</span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-4"
            style={{ fontFamily: "Chivo" }}
          >
            The Promoters Behind
            <br />
            Our Legacy
          </h2>
          <p className="text-base leading-relaxed text-[#64748B]">
            Experienced leadership committed to the <span className="font-bold text-[#D32F2F]">Hallmark of Quality</span> that
            defines every project we deliver.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {promoters.map((p, i) => (
            <div
              key={i}
              className={`border border-[#E2E8F0] p-8 bg-white hover:shadow-xl transition-all duration-300 group opacity-0 ${
                visible ? "animate-fade-in-up" : ""
              }`}
              style={{
                animationDelay: `${0.15 + i * 0.12}s`,
                animationFillMode: "forwards",
              }}
              data-testid={`promoter-card-${i}`}
            >
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center bg-[#0A0A0A] text-white flex-shrink-0 group-hover:bg-[#D32F2F] transition-colors duration-300">
                  {p.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3
                      className="text-lg font-bold text-[#0F172A] tracking-tight"
                      style={{ fontFamily: "Chivo" }}
                    >
                      {p.name}
                    </h3>
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] bg-[#FEF2F2] px-3 py-1" style={{ fontFamily: "Chivo" }}>
                      {p.tenure}
                    </span>
                  </div>
                  <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#D32F2F] mb-3" style={{ fontFamily: "Chivo" }}>
                    {p.role}
                  </div>
                  <p className="text-sm leading-relaxed text-[#64748B]">
                    {p.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`mt-12 bg-[#0A0A0A] p-8 md:p-10 opacity-0 ${visible ? "animate-fade-in-up" : ""}`}
          style={{ animationDelay: "0.7s", animationFillMode: "forwards" }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Star size={16} className="text-[#D32F2F]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D32F2F]" style={{ fontFamily: "Chivo" }}>
                  Hallmark of Quality
                </span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed max-w-lg">
                Started as a proprietorship in 2001, incorporated as Shrishaila Electricals (India) Pvt. Ltd.
                on 24th April 2009. Two decades of electrical engineering excellence, driven by qualified
                promoters with deep domain expertise.
              </p>
            </div>
            <div className="text-center flex-shrink-0">
              <div className="text-4xl font-black text-white" style={{ fontFamily: "Chivo" }}>
                2001
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold" style={{ fontFamily: "Chivo" }}>
                Founded
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── CONTACT ─── */
const Contact = () => {
  const [ref, visible] = useInView();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-32 bg-[#F4F4F5]"
      data-testid="contact-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Info */}
          <div
            className={`opacity-0 ${visible ? "animate-slide-left" : ""}`}
            style={{ animationFillMode: "forwards" }}
          >
            <span className="section-label">Get In Touch</span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0F172A] mb-6"
              style={{ fontFamily: "Chivo" }}
            >
              Let's Power Your
              <br />
              Next Project
            </h2>
            <p className="text-base leading-relaxed text-[#64748B] mb-10">
              Ready to discuss your electrical infrastructure needs? Get in touch with
              our team for project consultation, tenders, and quotations.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D32F2F] text-white flex-shrink-0">
                  <MapPin size={20} />
                </div>
                <div>
                  <div className="font-bold text-[#0F172A] text-sm uppercase tracking-wider mb-1" style={{ fontFamily: "Chivo" }}>
                    Head Office
                  </div>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    No. 8, NGEF, EHBCs Layout,
                    <br />
                    Nagarbhavi Main Road, Nagarbhavi,
                    <br />
                    Bangalore, Karnataka 560072
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D32F2F] text-white flex-shrink-0">
                  <Mail size={20} />
                </div>
                <div>
                  <div className="font-bold text-[#0F172A] text-sm uppercase tracking-wider mb-1" style={{ fontFamily: "Chivo" }}>
                    Email
                  </div>
                  <a
                    href="mailto:shrishailelectrical@yahoo.com"
                    className="text-sm text-[#64748B] hover:text-[#D32F2F] transition-colors"
                    data-testid="contact-email-link"
                  >
                    shrishailelectrical@yahoo.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D32F2F] text-white flex-shrink-0">
                  <Phone size={20} />
                </div>
                <div>
                  <div className="font-bold text-[#0F172A] text-sm uppercase tracking-wider mb-1" style={{ fontFamily: "Chivo" }}>
                    Phone
                  </div>
                  <a
                    href="tel:+918023182736"
                    className="text-sm text-[#64748B] hover:text-[#D32F2F] transition-colors"
                    data-testid="contact-phone-link"
                  >
                    +91 80-2318 2736
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div
            className={`opacity-0 ${visible ? "animate-slide-right" : ""}`}
            style={{ animationFillMode: "forwards", animationDelay: "0.2s" }}
          >
            <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white p-8 md:p-10 border border-[#E2E8F0]">
              <h3
                className="text-xl font-bold text-[#0F172A] mb-6 tracking-tight"
                style={{ fontFamily: "Chivo" }}
              >
                Request a Quote
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2" style={{ fontFamily: "Chivo" }}>
                    Name <span className="text-[#D32F2F]">*</span>
                  </label>
                  <input
                    type="text"
                    className="contact-input"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2" style={{ fontFamily: "Chivo" }}>
                    Email <span className="text-[#D32F2F]">*</span>
                  </label>
                  <input
                    type="email"
                    className="contact-input"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2" style={{ fontFamily: "Chivo" }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    className="contact-input"
                    placeholder="+91 XXXXX XXXXX"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2" style={{ fontFamily: "Chivo" }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    className="contact-input"
                    placeholder="Project inquiry"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    data-testid="contact-subject-input"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2" style={{ fontFamily: "Chivo" }}>
                  Message <span className="text-[#D32F2F]">*</span>
                </label>
                <textarea
                  className="contact-input"
                  rows="5"
                  placeholder="Describe your project requirements..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="cta-btn w-full justify-center"
                data-testid="contact-submit-button"
              >
                {sending ? "Sending..." : "Send Inquiry"} <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── FOOTER ─── */
const Footer = () => (
  <footer className="bg-[#0A0A0A] text-white py-16 md:py-20" data-testid="footer-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="Shrishaila Electricals" className="h-14 w-auto" />
            <div>
              <div className="font-black text-base tracking-tight" style={{ fontFamily: "Chivo" }}>
                SHRISHAILA ELECTRICALS
              </div>
              <div className="text-[10px] text-[#D32F2F] font-bold tracking-[0.15em] uppercase" style={{ fontFamily: "Chivo" }}>
                (India) Pvt. Ltd.
              </div>
              <div className="text-sm text-white/40 mt-1">
                ಶ್ರೀಶೈಲ ಎಲೆಕ್ಟ್ರಿಕಲ್ಸ್ (ಇಂಡಿಯಾ) ಪ್ರೈವೇಟ್ ಲಿಮಿಟೆಡ್
              </div>
            </div>
          </div>
          <p className="text-sm text-white/50 leading-relaxed max-w-xs">
            From Concept to Commissioning &mdash; our Hallmark of Quality powers
            Karnataka's electrical infrastructure with trust and precision since 2009.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h4
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#D32F2F] mb-6"
            style={{ fontFamily: "Chivo" }}
          >
            Quick Links
          </h4>
          <div className="space-y-3">
            {[
              { label: "About Us", href: "#about" },
              { label: "Services", href: "#services" },
              { label: "Clientele", href: "#clientele" },
              { label: "Promoters", href: "#promoters" },
              { label: "Contact", href: "#contact" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="block text-sm text-white/50 hover:text-white transition-colors"
                data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div>
          <h4
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#D32F2F] mb-6"
            style={{ fontFamily: "Chivo" }}
          >
            Certifications
          </h4>
          <div className="space-y-3 text-sm text-white/50">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[#D32F2F]" />
              Government of Karnataka &mdash; Class I
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[#D32F2F]" />
              BESCOM Approved Contractor
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[#D32F2F]" />
              CARE B+; Stable Rating
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[#D32F2F]" />
              KERC Safety Compliant
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-xs text-white/30">
          &copy; {new Date().getFullYear()} Shrishaila Electricals (India) Pvt. Ltd. All rights reserved.
        </p>
        <p className="text-xs text-white/30">
          CIN: U40108KA2009PTC049679
        </p>
      </div>
    </div>
  </footer>
);

/* ─── MAIN PAGE ─── */
const HomePage = () => (
  <>
    <Header />
    <Hero />
    <About />
    <Services />
    <Stats />
    <Clientele />
    <WhyChooseUs />
    <Promoters />
    <Contact />
    <Footer />
    <Toaster position="bottom-right" richColors />
  </>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
